import {
  IconCalendarDays,
  IconPageAdd,
  IconPencil,
  IconCircleX,
  Link,
  Modal,
  Tooltip,
  GreyGrey10,
  GreyGrey20,
  GreyGrey30,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  GreyWhite,
  Primary12Regular,
  Primary13SemiBold,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'
import { useBillingScheduleId } from 'BillingScheduleHome/utils/useBillingScheduleId'
import { ReactNode } from 'react'
import { SelectionScreen } from '../scheduleShortcuts.types'
import { useBillingScheduleAggregate } from 'BillingScheduleHome/utils/useBillingScheduleAggregate'
import { match } from 'ts-pattern'
import { parseBillingSchedulePhaseDuration } from '@sequencehq/utils'

const UpdateSelectionItem = (props: {
  icon: ReactNode
  title: string
  description: string
  onClick?: () => void
  disabled?: boolean
  disabledReason?: string
  ['data-testid']?: string
}) => {
  return (
    <Tooltip isDisabled={!props.disabled} label={props.disabledReason}>
      <Flex
        data-testid={props['data-testid']}
        alignItems="center"
        justifyContent="center"
        backgroundColor={GreyWhite}
        onClick={props.disabled ? () => null : props.onClick}
        cursor={props.disabled ? 'default' : 'pointer'}
        _hover={
          props.disabled
            ? {}
            : {
                '& .content': {
                  transform: 'scale(1.05)'
                },
                '& .icon': {
                  backgroundColor: GreyGrey30
                },
                backgroundColor: GreyGrey10
              }
        }
        userSelect="none"
        height="100%"
        width="100%"
        data-disabled={props.disabled}
      >
        <Flex
          className="content"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          transition="transform 0.3s 0.2s ease-in-out"
          opacity={props.disabled ? 0.5 : 1}
        >
          <Flex
            alignItems="center"
            justifyContent="center"
            height="32px"
            width="32px"
            borderRadius="6px"
            backgroundColor={GreyGrey20}
            marginBottom="16px"
            color={GreyGrey70}
            className="icon"
            transition="background-color 0.3s ease-in-out"
          >
            {props.icon}
          </Flex>
          <Box color={GreyGrey80} {...Primary13SemiBold}>
            {props.title}
          </Box>
          <Box color={GreyGrey60} {...Primary12Regular}>
            {props.description}
          </Box>
        </Flex>
      </Flex>
    </Tooltip>
  )
}

const useAvailableShortcuts = () => {
  const billingScheduleId = useBillingScheduleId()
  const billingScheduleAggregateQuery = useBillingScheduleAggregate({
    billingScheduleId,
    select: data => {
      const phaseDurations = data.billingSchedule.phases.map(phase => {
        const duration = parseBillingSchedulePhaseDuration({
          startDate: phase.startDate,
          endDate: phase.endDate
        })

        return duration
      })

      const finalPhaseDuration = phaseDurations.at(-1)

      if (!finalPhaseDuration) {
        return {
          finalPhase: {
            isOpenEnded: false,
            isMilestone: false
          },
          hasMilestonePhase: false
        }
      }

      return {
        finalPhase: {
          isOpenEnded: finalPhaseDuration === 'OPEN_ENDED',
          isMilestone: finalPhaseDuration === 'MILESTONE'
        },
        hasMilestonePhase: Boolean(phaseDurations.find(d => d === 'MILESTONE')),
        status: data.billingSchedule.status,
        isArchived: Boolean(data.billingSchedule.archivedAt)
      }
    }
  })

  return {
    renew: match({
      finalPhase: billingScheduleAggregateQuery.data?.finalPhase,
      hasMilestonePhase: billingScheduleAggregateQuery.data?.hasMilestonePhase
    })
      .with({ finalPhase: { isOpenEnded: true } }, () => ({
        disabled: true,
        reason: "Can't renew an open ended schedule."
      }))
      .with({ finalPhase: { isMilestone: true } }, () => ({
        disabled: true,
        reason: "Can't renew a schedule when final phase is a milestone phase."
      }))
      .otherwise(() => ({
        disabled: false,
        reason: undefined
      })),
    cancel: match({
      isArchived: billingScheduleAggregateQuery.data?.isArchived,
      status: billingScheduleAggregateQuery.data?.status
    })
      .with(
        {
          isArchived: true
        },
        () => ({ disabled: true, reason: `Can't cancel an archived schedule` })
      )
      .with(
        { status: 'DRAFT' },
        { status: 'COMPLETED' },
        { status: 'CANCELLED' },
        { status: 'SUSPENDED' },
        () => ({
          disabled: true,
          reason: `Only active or pending schedules can be cancelled.`
        })
      )
      .otherwise(() => ({
        disabled: false,
        reason: undefined
      })),
    upgrade: match({
      finalPhase: billingScheduleAggregateQuery.data?.finalPhase,
      hasMilestonePhase: billingScheduleAggregateQuery.data?.hasMilestonePhase
    })
      .with({ finalPhase: { isOpenEnded: true } }, () => ({
        disabled: true,
        reason: "Can't upgrade an open ended schedule."
      }))
      .with({ hasMilestonePhase: true }, () => ({
        disabled: true,
        reason: "Can't upgrade a schedule with a milestone phase."
      }))
      .otherwise(() => ({
        disabled: false,
        reason: undefined
      }))
  }
}

export const ShortcutSelectionScreen = (props: {
  onSelectScreen: (screen: SelectionScreen) => void
}) => {
  const billingScheduleId = useBillingScheduleId()
  const shortcuts = useAvailableShortcuts()

  return (
    <Modal.Content>
      <Grid
        gridTemplateRows="166px 166px"
        gridTemplateColumns="220px 220px"
        gridColumnGap="1px"
        gridRowGap="1px"
        backgroundColor={GreyGrey30}
        borderRadius="8px"
        border={`1px solid ${GreyGrey30}`}
        overflow="hidden"
      >
        <UpdateSelectionItem
          icon={<IconPageAdd />}
          data-testid="schedule.shortcut.upgrade"
          onClick={() => props.onSelectScreen('upgrade')}
          title="Upgrade"
          description="Add products or change pricing"
          disabled={shortcuts.upgrade.disabled}
          disabledReason={shortcuts.upgrade.reason}
        />
        <UpdateSelectionItem
          icon={<IconCalendarDays />}
          data-testid="schedule.shortcut.renew"
          onClick={() => props.onSelectScreen('renew')}
          title="Renew"
          description="Renew this schedule"
          disabled={shortcuts.renew.disabled}
          disabledReason={shortcuts.renew.reason}
        />
        <UpdateSelectionItem
          icon={<IconCircleX />}
          data-testid="schedule.shortcut.cancel"
          onClick={() => props.onSelectScreen('cancel')}
          title="End schedule"
          description="Change the end date"
          disabled={shortcuts.cancel.disabled}
          disabledReason={shortcuts.cancel.reason}
        />
        <Link.Unstyled to={`/billing-schedules/${billingScheduleId}/edit`}>
          <UpdateSelectionItem
            icon={<IconPencil />}
            data-testid="schedule.shortcut.custom"
            title="Other"
            description="Make custom changes"
          />
        </Link.Unstyled>
      </Grid>
    </Modal.Content>
  )
}
